import { useCallback, useState } from 'react';
import { isIOS, buildGmailComposeUrl } from '../lib/emailLaunch';

const APP_LAUNCH_TIMEOUT_MS = 2000;

/**
 * Launch "Open in Gmail" for a contact. iOS → the Gmail app scheme, with a
 * best-effort visibility/timer check: if the page isn't backgrounded within
 * ~2s we assume the app isn't installed and surface a fallback. Non-iOS → open
 * Gmail web in a new tab (always works, no fallback). iOS app-detection is a
 * heuristic; there is no reliable "is-app-installed" web API.
 */
export function useGmailLaunch(email: string, googleEmail: string | null | undefined) {
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const closeFallback = useCallback(() => setFallbackOpen(false), []);

  const launch = useCallback(() => {
    const ua = navigator.userAgent;
    const url = buildGmailComposeUrl(email, { googleEmail, userAgent: ua });
    if (!isIOS(ua)) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    let switched = false;
    const onHide = () => { switched = true; };
    document.addEventListener('visibilitychange', onHide, { once: true });
    window.location.assign(url);
    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onHide);
      if (!switched && document.visibilityState === 'visible') setFallbackOpen(true);
    }, APP_LAUNCH_TIMEOUT_MS);
  }, [email, googleEmail]);

  return { launch, fallbackOpen, closeFallback };
}
